/**
 * MESSAGES, at `/messages`. Built from `faithfully-kept-business/design/home/Messages.dc.html`, its
 * structure and its words, with her thread in place of the sample one, as GET /api/messages answers
 * it. The words are in `words.ts`.
 *
 * - THE ARTBOARD'S THREAD, oldest first and in the Worker's order: a line for each day, the "Your
 *   home is ready" card of each visit done with its note, unquoted, and "Cleaned by" the cleaner's
 *   first name under it, with no gap above it where there is no note, as Home says it (the fifth
 *   client-journey walk's leftovers, 2026-10-20; home #18, ops #98 phase 2), then each thing
 *   noticed on it as the office wrote it for her (`noticedLine`, home #19, ops #98 phase 3), each
 *   message the office sent her, and each message she wrote, on the right, with the office's
 *   reply under it once one has come. With several homes, which no artboard draws, a card names
 *   its home under its heading by the home's label alone (`homeNameOf`, provisional P40); with
 *   one, nothing.
 * - A LINK TO OPS'S INVOICE PAGE IS A WAY TO PAYMENTS. Ops never copies a link to its own pages:
 *   it writes `INVOICE_LINK_WORDS`, "[your invoice link]", in its place, since home shows the
 *   invoice itself (the seam, section 3 at d1c7636, l.412-414). For a person shown money each is
 *   drawn as `MESSAGES.invoiceLink`, "See it in Payments", a link to Payments in the sentence
 *   (`INLINE_LINK`), with what follows it up to the next space held on its line; every other word is
 *   as ops wrote it, in what the office sent her and in its answer to hers. For anybody else ops's
 *   words stay as they are (provisional P48). The owner's walk of 2026-10-16 met "Tap the link to
 *   tell us" beside the token drawn as dead text. Where the copy names the invoice it carries the
 *   link to (`invoice_id`, ops's section 11.4) and her copy holds it, as the Worker says, the link
 *   is to that invoice's own page instead, `MESSAGES.invoiceItself`, "See the invoice" (round six,
 *   slice S3, P86): the replay of the owner's household on 2026-10-18 found the list opened, three
 *   invoices of one day, none said to be the one.
 * - A COPY OF THE OFFICE'S TEXT THAT NAMES ITS CLEAN leads to it (slice M1 of the phase 1 plan,
 *   ops's section 11.4, `docs/seam.md` P104): under ops's words, a line of its own, "See this
 *   clean" (`MESSAGES.seeTheClean`), a link 48 tall to Schedule on that clean
 *   (`scheduleVisitPath`), clear of any link in the words above it. The Worker names the clean only
 *   where Schedule draws it and it is at one of her homes, so the link never opens a Schedule
 *   without it; a member reads it too, since her cleans are hers to see. Among several homes, a copy
 *   that names its home has the home's label over ops's words (`homeNameOf`, P40), in body ink as a
 *   ready card names one, so two reminders of one day read apart; with one home, a home she does
 *   not see, or one with no label, nothing. Ops's `kind` and `category` are drawn nowhere: a word
 *   for either would be home's own, and the copy's own words already say what it is.
 * - THE COPY OF AN EMAIL THE OFFICE SENT HER (slice M2 of the phase 1 plan, ops's section 11.1,
 *   `docs/seam.md` P105) stands beside the copies of her texts, in the Worker's order, once
 *   Microsoft took it: the Worker sends none that did not leave, and none to anybody else. Its
 *   subject, as ops wrote it, is the bubble's heading (`EmailHead`), and under it, in muted ink, the
 *   address it went to as ops cut it, "Email to k@example.com" (`MESSAGES.emailTo`), the one line
 *   home adds; then the home among several, ops's words byte for byte, linked as a text's are, "See
 *   this clean", and the signature, as above.
 * - A COPY OPS SAYS FAILED says so under it, on the left, in home's own words (`NOT_REACHED`,
 *   provisional P42): that it did not reach her, never why, with nothing to tap and no promise of
 *   another. The bubble and its signature stay, since what was sent is still what the office said.
 *   A copy handed off or delivered says nothing, so no screen claims a message was delivered.
 * - SIGNED AS THE ARTBOARD SIGNS: a copy of a message the office sent her is signed "Victoria,
 *   Faithfully Kept", with the first name of the person at the office ops names for it
 *   (`author_first_name`, the seam, l.382-384), and "Faithfully Kept" alone when ops names nobody,
 *   for a message the system composed. The office's answer to her own message carries no name from
 *   ops, so it is signed "Faithfully Kept" alone.
 * - NOT DRAWN: the artboard's subtitle, "Every reply is written by a person.", which a confirmation
 *   sent from a template may make untrue. The owner accepted it as built on 2026-09-28.
 * - ONLY HERS. The Worker sends only the copies addressed to her and the messages she wrote, and a
 *   message somebody else wrote is not drawn here even if an answer carried one.
 * - ASKING IS ALL THE COMPOSER DOES (CLAUDE.md binding rule 1). A message the door has written is
 *   drawn at once, as she wrote it, saying sent to the office, and takes the focus so that is read
 *   out; the place is read again, and the message goes on saying sent until the read carries it,
 *   even if that read did not go through. After that it says what ops says and nothing more:
 *   nothing once the office has it, the office's own words when they come, and a line only for a
 *   message of hers withdrawn, refused or never delivered.
 * - OPENED FROM HOME. "Ask us for something" leads here with the composer open (`WRITE_PATH`).
 * - A WAY TO WRITE ONLY WHEN THE WORKER OFFERS ONE: no composer for a person ops has not granted.
 * - ONE OPEN MESSAGE A HOUSEHOLD. Ops takes one at a time, counted on the household across every
 *   person on it, and refuses a second `409 already_asked` naming the first (the seam, section 4 at
 *   0b51e49, "The caps, held in the write", home's Q5), so while the Worker holds `message` the
 *   composer's place carries one line saying the last message from the household is still open,
 *   with the phone, and no way to write, for whoever on it reads the page. A message the door
 *   refuses so, somebody else's having gone first since the page was read, puts the seam's whole
 *   sentence naming the open one above that line at once, where the composer was, with the focus,
 *   and reads the place again; it stands while the place still holds the message, and the composer
 *   comes back once a read no longer does.
 * - NOTHING YET: before the household has come down it is the heading and the one line saying the
 *   rest is on its way, in the shell, so the nav still leads back to Home, with the composer when
 *   the Worker offers a message; an empty thread once it has says "Nothing right now." under the
 *   heading (`MESSAGES.nothingNow`), Payments' words for the same state, with the composer when the
 *   Worker offers one, until a message of hers is drawn. The holding screen, which has no nav, is
 *   Home's alone.
 * - THE NEWEST AT THE FOOT, where the page opens, as a thread is read.
 * - SEEN ONCE DRAWN (provisional P49). When the read says the office has said something since she
 *   last opened Messages, the page, once it has drawn the thread, tells the Worker she has seen it
 *   up to the newest thing the office said that the read gave (`markMessagesSeen`), once for each
 *   such read, so the nav stops counting it on every place; the read itself writes nothing. Its own
 *   tab counts nothing while she is here (`components/BottomNav.tsx`).
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  getMessages,
  markMessagesSeen,
  type EmailSaid,
  type HomeName,
  type Journal,
  type MessageRequest,
  type MessagesPlace,
  type MessageState,
  type ThreadItem,
} from '../api.ts';
import { INLINE_LINK, TEXT_LINK } from '../components/buttons.ts';
import { COLUMN } from '../components/column.ts';
import { Composer } from '../components/Composer.tsx';
import { Guarded, type Signed } from '../components/Guarded.tsx';
import { Phone } from '../components/Phone.tsx';
import { Report, reportSays } from '../components/Report.tsx';
import { Shell } from '../components/Shell.tsx';
import { dayInWords } from '../lib/dates.ts';
import { homeNameOf } from '../lib/visits.ts';
import { invoicePath, PAYMENTS_PATH, scheduleVisitPath } from '../paths.ts';
import {
  alreadyAskedWords,
  FAILED_BEFORE_PHONE,
  HOME,
  INVOICE_LINK_WORDS,
  LOOKS_LIKE_A_CODE,
  MESSAGE_OPEN_BEFORE_PHONE,
  MESSAGES,
  NOT_REACHED,
  REQUEST,
  REST_ON_ITS_WAY,
  signedBy,
} from '../words.ts';

export function Messages(signed: Signed) {
  return (
    <Guarded load={getMessages} signed={signed} at="messages" heading={MESSAGES.heading}>
      {(place, reload) => <MessagesDrawn place={place} signed={signed} reload={reload} />}
    </Guarded>
  );
}

interface DrawnProps {
  readonly place: MessagesPlace;
  readonly signed: Signed;
  readonly reload: () => void;
}

/** A message the door wrote from this page: its request id, and her words. */
interface SentHere {
  readonly id: string;
  readonly body: string;
}

/**
 * A message the door refused from this page because one, anybody's on the household, is still open:
 * the open one's request id, when the door named it, and the place as it was read when it did.
 */
interface RefusedHere {
  readonly first?: string;
  readonly place: MessagesPlace;
}

function MessagesDrawn({ place, signed, reload }: DrawnProps) {
  const [params] = useSearchParams();
  /** Each message the door wrote from this page, in the order sent. */
  const [sentHere, setSentHere] = useState<readonly SentHere[]>([]);
  /** The request whose message takes the focus, having just been sent. */
  const [focusOn, setFocusOn] = useState<string | null>(null);
  /** The last message the door refused from this page as already asked. */
  const [refused, setRefused] = useState<RefusedHere | null>(null);

  const carried = new Set(
    place.thread.flatMap((item) => (item.kind === 'request' ? [item.request.id] : [])),
  );
  const waiting: ThreadItem[] = sentHere
    .filter((each) => !carried.has(each.id))
    .map((each) => ({
      kind: 'request',
      day: place.today,
      request: {
        id: each.id,
        type: 'message',
        mine: true,
        state: 'sent',
        daysThatSuit: [],
        body: each.body,
      },
    }));
  const shown = [
    ...place.thread.filter(
      (item) => item.kind !== 'request' || (item.request.mine && item.request.type === 'message'),
    ),
    ...waiting,
  ];

  const sent = (id: string, body: string) => {
    setSentHere((held) => [...held, { id, body }]);
    setFocusOn(id);
    reload();
  };

  const alreadyAsked = (first?: string) => {
    setRefused(first === undefined ? { place } : { first, place });
    reload();
  };

  // Drawn: she has seen what the office said, up to the newest thing this read gave, once for each
  // read that says something is new.
  useEffect(() => {
    const newest = place.newestFromOffice;
    if ((place.household.newMessages ?? 0) > 0 && newest !== undefined) {
      void markMessagesSeen(place.household.id, newest);
    }
  }, [place]);

  // The door's refusal stands until a read after it says the household's message is no longer open:
  // on the place it was refused on, which no read has replaced yet, and on any read that holds it.
  // Once one does not, it is over, and a message held again later is another, which it never names.
  const open = place.held.includes('message');
  const refusedNow = refused !== null && (refused.place === place || open) ? refused : null;
  useEffect(() => {
    if (refused !== null && refusedNow === null) setRefused(null);
  }, [refused, refusedNow]);

  return (
    <Shell
      at="messages"
      payments={place.household.payments}
      foot={
        place.offers.includes('message') && refusedNow === null ? (
          <Composer
            household={place.household.id}
            open={params.has('write')}
            onSent={sent}
            onSignedOut={signed.onSignedOut}
            onStale={reload}
            onAlreadyAsked={alreadyAsked}
          />
        ) : open || refusedNow !== null ? (
          <MessageOpen first={refusedNow?.first} />
        ) : undefined
      }
    >
      <h1 className="font-serif text-h2 font-semibold text-text-body">{MESSAGES.heading}</h1>
      {place.arriving.messages && place.thread.length === 0 ? (
        <p className="text-body text-pretty">{REST_ON_ITS_WAY}</p>
      ) : !place.arriving.messages && shown.length === 0 ? (
        <p className="text-body text-pretty">{MESSAGES.nothingNow}</p>
      ) : null}
      <Thread
        items={shown}
        homes={place.homes}
        payments={place.household.payments === true}
        focusOn={focusOn}
      />
    </Shell>
  );
}

/**
 * Where the composer sits, while the household's last message is still open with the office. When
 * the door has just refused hers for it, naming the open one `first`, the seam's sentence comes
 * first and takes the focus, so the refusal is read out where the composer she pressed Send in was.
 * Its bar runs the whole width and its lines are in the page's one column, as the composer's are.
 */
function MessageOpen({ first }: { first?: string | undefined }) {
  const said = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    if (first !== undefined) said.current?.focus();
  }, [first]);

  return (
    <div className="border-t border-border-subtle bg-surface-raised py-3">
      <div className={`${COLUMN} px-4`}>
        {first !== undefined ? (
          <p
            ref={said}
            tabIndex={-1}
            className="mb-2 text-small text-pretty text-text-body wrap-break-word"
          >
            {alreadyAskedWords(first)}
          </p>
        ) : null}
        <p className="text-small text-pretty text-text-body">
          {MESSAGE_OPEN_BEFORE_PHONE} <Phone after="." />
        </p>
      </div>
    </div>
  );
}

/** The items of one day, under the line that names it. */
interface Day {
  readonly key: string;
  readonly day: string;
  readonly items: ThreadItem[];
}

/** The thread in its order, a new day wherever the day changes. */
function byDay(items: readonly ThreadItem[]): Day[] {
  const days: Day[] = [];
  const seen = new Map<string, number>();
  for (const item of items) {
    const last = days.at(-1);
    if (last?.day === item.day) {
      last.items.push(item);
      continue;
    }
    const times = (seen.get(item.day) ?? 0) + 1;
    seen.set(item.day, times);
    days.push({
      key: times === 1 ? item.day : `${item.day}#${times}`,
      day: item.day,
      items: [item],
    });
  }
  return days;
}

/** The key an item keeps from one read to the next, so a message sent here keeps its focus. */
function keyOf(item: ThreadItem): string {
  switch (item.kind) {
    case 'journal':
      return `journal:${item.visit.id}`;
    case 'message':
      // An email's copy and a text's are kept apart, whatever ids ops gave them.
      return `${item.message.email === undefined ? 'message' : 'email'}:${item.message.id}`;
    case 'request':
      return `request:${item.request.id}`;
  }
}

interface ThreadProps {
  readonly items: readonly ThreadItem[];
  /** The homes she may see, which name a card's home, or a copy's, among several. */
  readonly homes: readonly HomeName[];
  /** Whether she is shown money, which draws ops's invoice link as a way to Payments. */
  readonly payments: boolean;
  readonly focusOn: string | null;
}

function Thread({ items, homes, payments, focusOn }: ThreadProps) {
  useEffect(() => {
    // Where the newest is. Only when first drawn: after that the page stays where she put it.
    const page = document.scrollingElement;
    page?.scrollTo?.({ top: page.scrollHeight });
  }, []);

  return (
    <>
      {byDay(items).map(({ key, day, items: said }) => {
        const words = dayInWords(day);
        return (
          <section key={key} className="flex flex-col gap-3">
            {words ? <h2 className="text-center text-small text-text-muted">{words}</h2> : null}
            <ol className="flex flex-col gap-3">
              {said.map((item) => (
                <li key={keyOf(item)} className="flex flex-col">
                  {item.kind === 'journal' ? (
                    <Ready
                      visit={item.visit}
                      journal={item.journal}
                      home={homeNameOf(item.visit, homes)}
                    />
                  ) : item.kind === 'message' ? (
                    <FromOffice
                      words={item.message.body}
                      author={item.message.author}
                      state={item.message.state}
                      payments={payments}
                      invoiceId={item.message.invoiceId}
                      visitId={item.message.visitId}
                      home={homeNameOf(item.message, homes)}
                      email={item.message.email}
                    />
                  ) : (
                    <Hers
                      request={item.request}
                      payments={payments}
                      focus={item.request.id === focusOn}
                    />
                  )}
                </li>
              ))}
            </ol>
          </section>
        );
      })}
    </>
  );
}

/**
 * The card a visit done sends: "Your home is ready", its home's name when she sees several, the
 * note, and "Cleaned by" the cleaner's first name under it, with no gap above it where there is no
 * note, as Home's last clean says it. The note is the office's words for her, never the cleaner's
 * own (home #18, ops #98 phase 2), so it is drawn with no quotation marks and never with "From" her
 * under it, as the artboard drew the cleaner's own words. Under them, each thing noticed on the
 * clean, the office's line for her in ops's order (home #19, ops #98 phase 3), as "10. Your home is
 * ready, on Messages" draws "Noticed: a dripping tap in the guest bath.": the note's size, the
 * muted ink and the card's 10px under what is above it, none above the first where nothing is.
 */
function Ready({
  visit,
  journal,
  home,
}: {
  visit: { readonly id: string; readonly cleaner?: string };
  journal: Journal;
  home: string | undefined;
}) {
  const said = reportSays({ journal, cleaner: visit.cleaner });
  return (
    <article
      data-from="cleaner"
      className="overflow-hidden rounded-card border-2 border-border-brand bg-surface-raised"
    >
      <div
        className={`bg-surface-brand-soft px-4 py-2.5 ${said ? 'border-b-2 border-border-brand' : ''}`}
      >
        <h3 className="text-body font-semibold text-text-brand">{HOME.ready}</h3>
        {home === undefined ? null : <p className="text-body text-text-body">{home}</p>}
      </div>
      {said ? (
        <div className="px-4 py-3">
          {/* The note, "Cleaned by" and each thing noticed, as every report of a clean draws them. */}
          <Report journal={journal} cleaner={visit.cleaner} small />
        </div>
      ) : null}
    </article>
  );
}

interface FromOfficeProps {
  readonly words: string;
  readonly author?: string | undefined;
  /** Where ops last said a copy's delivery stands; none for the office's answer to her message. */
  readonly state?: MessageState | undefined;
  /** Whether she is shown money, which draws ops's invoice link as a way to Payments. */
  readonly payments: boolean;
  /** The invoice the copy carries ops's link to, where her copy holds it (round six, slice S3). */
  readonly invoiceId?: string | undefined;
  /** The clean the copy is about, where Schedule draws it (slice M1). */
  readonly visitId?: string | undefined;
  /** The name of the home the copy is about, when she sees several homes and it has one. */
  readonly home?: string | undefined;
  /** On the copy of an email alone: its subject, and the address it went to (slice M2). */
  readonly email?: EmailSaid | undefined;
}

/**
 * The link drawn for ops's invoice link, with what follows it, held on its line: to the invoice's own
 * page where the copy names one her copy holds (round six, slice S3, P86), else to Payments.
 */
function InvoiceLink({
  after,
  invoiceId,
}: {
  readonly after: string;
  readonly invoiceId: string | undefined;
}) {
  return (
    <span className="whitespace-nowrap">
      <Link
        to={invoiceId === undefined ? PAYMENTS_PATH : invoicePath(invoiceId)}
        className={INLINE_LINK}
      >
        {invoiceId === undefined ? MESSAGES.invoiceLink : MESSAGES.invoiceItself}
      </Link>
      {after}
    </span>
  );
}

/**
 * Ops's words, each `INVOICE_LINK_WORDS` in them drawn as a link for a person shown money, to the
 * invoice the copy names where it names one, else to Payments, and every other word as ops wrote
 * it. What follows a link up to the next space, a full stop as a rule, is held with it on its line,
 * as `Phone` holds its own; anybody shown no money reads ops's words as they are.
 */
function linked(words: string, payments: boolean, invoiceId?: string): ReactNode {
  if (!payments || !words.includes(INVOICE_LINK_WORDS)) return words;
  const [first, ...rest] = words.split(INVOICE_LINK_WORDS);
  return [
    first,
    ...rest.flatMap((after, index) => {
      const held = /^\S*/.exec(after)![0];
      return [
        <InvoiceLink key={index} after={held} invoiceId={invoiceId} />,
        after.slice(held.length),
      ];
    }),
  ];
}

/**
 * What the office said, on the left: the home it is about over it where she sees several, then
 * ops's words, then "See this clean" where it names a clean Schedule draws, signed with the first
 * name of the person in the office ops names for it, or by the business alone when it names nobody;
 * and, under a copy ops says failed, that it did not reach her. The copy of an email opens with its
 * subject, its heading, and the address it went to under it (slice M2).
 */
function FromOffice({
  words,
  author,
  state,
  payments,
  invoiceId,
  visitId,
  home,
  email,
}: FromOfficeProps) {
  return (
    <>
      <div
        data-from="office"
        data-channel={email === undefined ? undefined : 'email'}
        className="max-w-[86%] self-start rounded-card rounded-bl-sm border border-border-subtle bg-surface-raised px-3.5 py-2.5"
      >
        {email === undefined ? null : <EmailHead email={email} />}
        {home === undefined ? null : (
          <p className="text-small font-semibold text-text-body">{home}</p>
        )}
        <p className="text-small whitespace-pre-wrap text-text-body wrap-break-word">
          {linked(words, payments, invoiceId)}
        </p>
        {visitId === undefined ? null : (
          // Its own line, 12 below the words, so its box never meets a link's in the line above.
          <p className="mt-3">
            <Link to={scheduleVisitPath(visitId)} className={`text-small ${TEXT_LINK}`}>
              {MESSAGES.seeTheClean}
            </Link>
          </p>
        )}
        <p className="mt-1 text-small text-text-muted">{signedBy(author)}</p>
      </div>
      {state === 'failed' ? (
        <p data-said className="mt-1 self-start text-small text-pretty text-text-muted">
          {NOT_REACHED}
        </p>
      ) : null}
    </>
  );
}

/**
 * The head of the copy of an email (slice M2, P105): its subject, ops's own words, as the bubble's
 * heading, a step up from the words under it, and the address it went to as ops cut it under that
 * in muted ink, "Email to k@example.com", so she can tell it from a text's copy and find it in her
 * own inbox; each only where it came. Eight below it, the home's name or ops's words.
 */
function EmailHead({ email }: { readonly email: EmailSaid }) {
  if (email.subject === undefined && email.toMasked === undefined) return null;
  return (
    <div className="mb-2">
      {email.subject === undefined ? null : (
        <h3 className="text-body font-semibold text-pretty text-text-body wrap-break-word">
          {email.subject}
        </h3>
      )}
      {email.toMasked === undefined ? null : (
        <p className="text-small text-text-muted wrap-break-word">
          {MESSAGES.emailTo(email.toMasked)}
        </p>
      )}
    </div>
  );
}

/** What is said under her own message: a line, and whether the phone follows it. */
interface Said {
  readonly text: string;
  readonly phone?: boolean;
}

/**
 * Where her message stands, in words, or null when there is nothing to say: once the office has it
 * there is nothing to confirm, and a decision is only ever the office's own words, drawn as its
 * reply.
 */
function saidUnder(request: MessageRequest): Said | null {
  switch (request.state) {
    case 'sent':
      return { text: REQUEST.sent };
    case 'cancelled':
      return request.reason === 'withdrawn' ? { text: REQUEST.withdrawn } : null;
    case 'refused':
      return request.code === 'looks_like_a_code'
        ? { text: LOOKS_LIKE_A_CODE }
        : { text: FAILED_BEFORE_PHONE, phone: true };
    case 'stopped':
      return { text: FAILED_BEFORE_PHONE, phone: true };
    default:
      return null;
  }
}

interface HersProps {
  readonly request: MessageRequest;
  /** Whether she is shown money, which draws ops's invoice link as a way to Payments. */
  readonly payments: boolean;
  readonly focus: boolean;
}

/** Her message, on the right, with where it stands and the office's reply. */
function Hers({ request, payments, focus }: HersProps) {
  const group = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (focus) group.current?.focus();
  }, [focus]);

  const said = saidUnder(request);
  return (
    // The margin below keeps it clear of the composer and the nav when the focus scrolls to it.
    <div ref={group} tabIndex={-1} className="flex scroll-mb-40 flex-col">
      {request.body !== undefined ? (
        <div
          data-from="you"
          className="max-w-[82%] self-end rounded-card rounded-br-sm bg-surface-brand px-3.5 py-2.5"
        >
          <p className="text-small whitespace-pre-wrap text-text-on-brand wrap-break-word">
            {request.body}
          </p>
        </div>
      ) : null}
      {said ? (
        <p data-said className="mt-1 self-end text-right text-small text-pretty text-text-muted">
          {said.text}
          {said.phone ? (
            <>
              {' '}
              <Phone after="." />
            </>
          ) : null}
        </p>
      ) : null}
      {request.answer !== undefined ? (
        <div className="mt-3 flex flex-col">
          <FromOffice words={request.answer} payments={payments} />
        </div>
      ) : null}
    </div>
  );
}
